import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { type ChatGroup, groupChats } from '@goaltree/backend/convex/lib/coachTitle'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@goaltree/ui/components/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@goaltree/ui/components/dropdown-menu'
import { cn } from '@goaltree/ui/lib/utils'
import { useMutation, usePaginatedQuery, useQuery } from 'convex/react'
import { MoreHorizontal, Pencil, Pin, PinOff, Search, Trash2, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  optimisticDeleteThread,
  optimisticRenameThread,
  optimisticSetThreadPinned,
} from '@/lib/coach-optimistic'

type ChatId = Id<'coachThreads'> | null
type Row = { id: ChatId; title: string; updatedAt: number; pinned: boolean; snippet?: string }

const RECENT_COUNT = 8
const PAGE = 25

function useDebounced<T>(value: T, ms: number) {
  const [out, setOut] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setOut(value), ms)
    return () => clearTimeout(id)
  }, [value, ms])
  return out
}

export function CoachChatList({
  threads,
  activeId,
  onOpen,
  formatDate,
}: {
  threads: Row[]
  activeId: ChatId
  onOpen: (id: ChatId) => void
  formatDate: (ts: number) => string
}) {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [expanded, setExpanded] = useState(false)
  const q = useDebounced(query.trim(), 250)
  const searching = q.length >= 2
  const hits = useQuery(api.coachHistory.searchChats, searching ? { q } : 'skip')
  const pages = usePaginatedQuery(
    api.coachHistory.listThreadsPage,
    expanded && !searching ? {} : 'skip',
    { initialNumItems: PAGE },
  )
  const requestTitles = useMutation(api.coachHistory.requestTitles)
  useEffect(() => {
    void requestTitles({}).catch(() => {})
  }, [requestTitles])

  const merged = new Map<string, Row>()
  for (const r of [...threads, ...(expanded ? pages.results : [])]) merged.set(r.id ?? 'default', r)
  const all = [...merged.values()].sort((a, b) => b.updatedAt - a.updatedAt)
  let unpinned = 0
  const shown = expanded ? all : all.filter((r) => r.pinned || unpinned++ < RECENT_COUNT)

  function onScroll(e: React.UIEvent<HTMLDivElement>) {
    const el = e.currentTarget
    if (pages.status === 'CanLoadMore' && el.scrollHeight - el.scrollTop - el.clientHeight < 80)
      pages.loadMore(PAGE)
  }

  const groupLabel: Record<ChatGroup, string> = {
    pinned: t('coach.chatList.pinned'),
    today: t('coach.chatList.today'),
    yesterday: t('coach.chatList.yesterday'),
    week: t('coach.chatList.thisWeek'),
    older: t('coach.chatList.older'),
  }

  return (
    <div className="flex min-h-0 flex-col">
      <div className="relative mx-1 mb-1 mt-1.5">
        <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Escape' && query && (e.stopPropagation(), setQuery(''))}
          placeholder={t('coach.chatList.search')}
          aria-label={t('coach.chatList.search')}
          className="h-8 w-full rounded-lg border border-border/60 bg-muted/40 pl-7 pr-7 text-xs outline-none placeholder:text-muted-foreground/70 focus:border-border"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery('')}
            aria-label={t('coach.chatList.clearSearch')}
            className="absolute right-1.5 top-1/2 flex size-5 -translate-y-1/2 cursor-pointer items-center justify-center rounded text-muted-foreground hover:text-foreground"
          >
            <X className="size-3" />
          </button>
        )}
      </div>
      <div
        onScroll={expanded ? onScroll : undefined}
        className={cn(
          'min-h-0 overflow-y-auto',
          expanded || searching ? 'max-h-[60vh]' : 'max-h-[45vh]',
        )}
      >
        {searching ? (
          hits === undefined ? null : hits.length === 0 ? (
            <p className="px-2.5 py-3 text-center text-muted-foreground">
              {t('coach.chatList.noResults')}
            </p>
          ) : (
            hits.map((r) => (
              <ChatRowItem
                key={r.id ?? 'default'}
                row={r}
                active={r.id === activeId}
                onOpen={onOpen}
                date={formatDate(r.updatedAt)}
              />
            ))
          )
        ) : (
          groupChats(shown).map(({ group, rows }) => (
            <div key={group}>
              <p className="px-2.5 pb-1 pt-2 text-[9.5px] font-bold uppercase tracking-[0.12em] text-muted-foreground/70">
                {groupLabel[group]}
              </p>
              {rows.map((r) => (
                <ChatRowItem
                  key={r.id ?? 'default'}
                  row={r}
                  active={r.id === activeId}
                  onOpen={onOpen}
                  date={formatDate(r.updatedAt)}
                />
              ))}
            </div>
          ))
        )}
        {expanded && !searching && pages.status === 'LoadingMore' && (
          <p className="px-2.5 py-2 text-center text-[10px] text-muted-foreground">…</p>
        )}
      </div>
      {!searching && (expanded || all.length > shown.length) && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex cursor-pointer items-center rounded-lg px-2.5 py-2 text-left text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          {expanded ? t('coach.showLess') : `${t('coach.allChats')} ›`}
        </button>
      )}
    </div>
  )
}

function ChatRowItem({
  row,
  active,
  onOpen,
  date,
}: {
  row: Row
  active: boolean
  onOpen: (id: ChatId) => void
  date: string
}) {
  const { t } = useTranslation()
  const rename = useMutation(api.coachHistory.renameThread).withOptimisticUpdate(
    optimisticRenameThread,
  )
  const setPinned = useMutation(api.coachHistory.setThreadPinned).withOptimisticUpdate(
    optimisticSetThreadPinned,
  )
  const remove = useMutation(api.coachHistory.deleteThread).withOptimisticUpdate(
    optimisticDeleteThread,
  )
  const [editing, setEditing] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [draft, setDraft] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const title = row.title || t('coach.newChat')

  useEffect(() => {
    if (!editing) return
    const id = setTimeout(() => inputRef.current?.select(), 120)
    return () => clearTimeout(id)
  }, [editing])

  function save() {
    setEditing(false)
    const next = draft.trim()
    if (next && next !== row.title) void rename({ threadId: row.id, title: next }).catch(() => {})
  }

  if (editing)
    return (
      <div className="px-1 py-0.5">
        <input
          ref={inputRef}
          value={draft}
          maxLength={60}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save()
            if (e.key === 'Escape') {
              e.stopPropagation()
              setEditing(false)
            }
          }}
          aria-label={t('coach.chatList.rename')}
          className="h-8 w-full rounded-lg border border-primary/50 bg-background px-2 text-xs outline-none"
        />
      </div>
    )

  return (
    <div className="group relative">
      <button
        type="button"
        onClick={() => onOpen(row.id)}
        className={cn(
          'flex w-full cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-left text-foreground hover:bg-muted',
          active && 'bg-muted font-medium',
        )}
      >
        <span className="min-w-0 flex-1">
          <span className="block truncate">{title}</span>
          {row.snippet && (
            <span className="block truncate text-[10.5px] font-normal text-muted-foreground">
              {row.snippet}
            </span>
          )}
        </span>
        <span className="shrink-0 text-[10px] font-normal text-muted-foreground/70 group-hover:invisible [@media(hover:none)]:invisible">
          {date}
        </span>
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label={t('coach.chatList.options')}
          className="absolute right-1.5 top-1.5 flex size-6 cursor-pointer items-center justify-center rounded-md text-muted-foreground opacity-0 hover:bg-background hover:text-foreground group-hover:opacity-100 data-popup-open:opacity-100 [@media(hover:none)]:opacity-100"
        >
          <MoreHorizontal className="size-3.5" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-36 rounded-lg p-1 shadow-xl">
          <DropdownMenuItem
            className="cursor-pointer rounded-md"
            onClick={() => {
              setDraft(row.title)
              setEditing(true)
            }}
          >
            <Pencil className="size-3.5" /> {t('coach.chatList.rename')}
          </DropdownMenuItem>
          <DropdownMenuItem
            className="cursor-pointer rounded-md"
            onClick={() =>
              void setPinned({ threadId: row.id, pinned: !row.pinned }).catch(() => {})
            }
          >
            {row.pinned ? <PinOff className="size-3.5" /> : <Pin className="size-3.5" />}
            {row.pinned ? t('coach.chatList.unpin') : t('coach.chatList.pin')}
          </DropdownMenuItem>
          <DropdownMenuItem
            variant="destructive"
            className="cursor-pointer rounded-md"
            onClick={() => setConfirmDelete(true)}
          >
            <Trash2 className="size-3.5" /> {t('coach.chatList.delete')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('coach.chatList.deleteConfirm', { title })}</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => void remove({ threadId: row.id }).catch(() => {})}>
              {t('coach.chatList.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
